import clsx from 'clsx';
import { IconButton, IconType, Tooltip } from 'copilot-design-system';

import { useContextBar } from 'src/hooks/useContextBar';

export interface NavTab<T> {
  key: T;
  label: string;
  icon: IconType;
  disabled?: boolean;
}

interface ContextBarNavTabsProps<T> {
  tabs: Array<NavTab<T>>;
  activeTabKey: T;
  onTabChange: (newValue: T) => void;
  className?: string;
}

/**
 * ContextBarNavTabs component renders vertical menu tabs using IconButtons
 * @returns {React.ReactElement}
 */
export const ContextBarNavTabs = <T,>({
  activeTabKey,
  onTabChange,
  tabs,
  className,
}: ContextBarNavTabsProps<T>) => {
  const { open, setOpen } = useContextBar();

  return (
    <div className={clsx('flex flex-col items-center gap-2', className)}>
      {tabs.map((tab) => {
        const isActive = open && activeTabKey === tab.key;
        return (
          <Tooltip
            key={String(tab.key)}
            content={tab.label}
            anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
            transformOrigin={{ vertical: 'center', horizontal: 'right' }}
            baseOffset={2}
            showDelayDuration={500}
          >
            <IconButton
              className={clsx(
                isActive ? 'bg-[#E9EBEE]' : 'hover:bg-secondary-hover',
              )}
              icon={tab.icon}
              label={tab.label}
              variant="minimal"
              onClick={() => {
                if (isActive) {
                  setOpen(false);
                } else {
                  setOpen(true);
                  onTabChange(tab.key);
                }
              }}
              disabled={tab.disabled}
            />
          </Tooltip>
        );
      })}
    </div>
  );
};
